<template>
  <div v-if="project" class="forms-elements">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />
    <ProjectTimeline :project="project" />
    <b-card-group deck class="project-cards-tight">
      <ProjectCardOverview :project="project" :projectNumber="projectNumberByClientContext" />
      <ProjectCardState :project="project" />
      <ProjectCardInfo :project="project" />
      <!-- <ProjectCardCommunication :project="project" /> -->
      <!-- <ProjectCardPosts :project="project" /> -->
    </b-card-group>

    <b-tabs ref="tabs" content-class="" fill v-model="tabIndex">
      <template v-if="isGuest">
        <b-tab ref="customer" data-key="customer" :title="$t('customer')">
          <ProjectCustomer :project="project" />
        </b-tab>

        <b-tab ref="communication" data-key="communication" :title="$t('communication')">
          <ProjectCommunication :project="project" />
        </b-tab>

        <b-tab ref="configuration" data-key="configuration" :title="$t('configuration')">
          <ProjectSolarPlant :project="project" />
        </b-tab>

        <b-tab ref="technicalReview" :title="$t('technicalReview')" data-key="technicalReview">
          <ProjectTechnicalReview :project="project" />
        </b-tab>

        <b-tab ref="installation" data-key="installation" :title="$t('installation')">
          <ProjectInstallation :project="project" />
        </b-tab>

        <b-tab ref="documents" data-key="documents" :title="$t('documents')">
          <ProjectDocuments :project="project" />
        </b-tab>

        <b-tab :title="$t('measurement')">
          <ProjectMeasurementReport
            :project="project"
            v-on:project-measurement-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab :title="$t('acceptanceReport')">
          <ProjectAcceptanceReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>
        <b-tab :title="$t('serviceReport')">
          <ProjectServiceReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-row>
          <b-col lg="12" md="12" sm="12">
            <ButtonBar
              :showPreviousButton="buttons.showPreviousButton"
              :showNextButton="buttons.showNextButton"
              :showDeleteButton="buttons.showDeleteButton"
              :model="project"
              modelEditRoute="ProjectEditPage"
              modelListRoute="ProjectsPaginatedPage"
              modelRouteParamName="projectNumber"
              :nextModelNumber="project.nextProjectNumber"
              :previousModelNumber="project.previousProjectNumber"
              :updateCallback="updateProject"
              :createCallback="createProject"
              :deleteCallback="deleteProject"
              :fetchCallback="fetchProjects"
            />
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <b-tab
          v-if="canSeeProjectInternalTabs"
          ref="internal"
          data-key="internal"
          :title="$t('internal')"
        >
          <ProjectInternal
            :project="project"
            v-on:project-short-teaser-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab
          v-if="showIabCalculationTab"
          ref="iabCalculation"
          data-key="iabCalculation"
          :title="$t('iabCalculation')"
        >
          <ProjectInvestmentDeductionAllowance
            :project="project"
            v-on:project-iab-export-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab ref="customer" data-key="customer" :title="$t('customer') + ' / ' + $t('parties')">
          <ProjectCustomer :project="project" />
        </b-tab>

        <b-tab ref="communication" data-key="communication" :title="$t('communication')">
          <ProjectCommunication :project="project" :selectedAppointment="selectedAppointment" />
        </b-tab>

        <b-tab ref="configuration" data-key="configuration" :title="$t('configuration')">
          <ProjectSolarPlant :project="project" />
        </b-tab>

        <b-tab ref="technicalReview" :title="$t('technicalReview')" data-key="technicalReview">
          <ProjectTechnicalReview :project="project" />
        </b-tab>

        <b-tab ref="items" :title="$t('items')" data-key="items">
          <ProjectMaterials :project="project" />
        </b-tab>

        <b-tab ref="installation" data-key="installation" :title="$t('installation')">
          <ProjectInstallation :project="project" />
        </b-tab>

        <b-tab :title="$t('powerCompany')">
          <ProjectPowerCompany :project="project" />
        </b-tab>

        <b-tab
          ref="registration"
          data-key="registration"
          v-if="isAdmin"
          :title="$t('registration')"
        >
          <ProjectRegistration :project="project" />
        </b-tab>

        <b-tab
          v-if="canSeeProjectInternalTabs"
          ref="offers_invoices"
          data-key="offers_invoices"
          :title="$t('offers_invoices')"
        >
          <ProjectAccounting :project="project" />
        </b-tab>

        <b-tab ref="Freigabe" data-key="Freigabe" v-if="isAdmin" title="Freigabe">
          <EmployerApproval :project="project" />
        </b-tab>

        <b-tab ref="documents" data-key="documents" :title="$t('documents')">
          <ProjectDocuments :project="project" />
        </b-tab>

        <b-tab ref="bank" data-key="bank" :title="$t('bank')">
          <ProjectBank :project="project" />
        </b-tab>

        <b-tab ref="measurement" data-key="measurement" :title="$t('measurementReport')">
          <ProjectMeasurementReport
            :project="project"
            v-on:project-measurement-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab ref="acceptanceReport" data-key="acceptanceReport" :title="$t('acceptanceReport')">
          <ProjectAcceptanceReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab ref="serviceReport" data-key="serviceReport" :title="$t('serviceReport')">
          <ProjectServiceReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab ref="qualityReport" data-key="qualityReport" :title="$t('qualityReport')">
          <ProjectQualityReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab v-if="isAdmin" :title="$t('assessmentReport')">
          <ProjectAssessmentReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab :title="$t('salesReport')">
          <ProjectSalesReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab v-if="isAdmin && !isNew" :title="$t('audit_log')">
          <ProjectAuditLog :project="project" />
        </b-tab>

        <b-row>
          <b-col lg="12" md="12" sm="12">
            <ButtonBar
              :showPreviousButton="buttons.showPreviousButton"
              :showNextButton="buttons.showNextButton"
              :showDeleteButton="buttons.showDeleteButton"
              :model="project"
              modelEditRoute="ProjectEditPage"
              modelListRoute="ProjectsPaginatedPage"
              modelRouteParamName="projectNumber"
              :nextModelNumber="project.nextProjectNumber"
              :previousModelNumber="project.previousProjectNumber"
              :updateCallback="updateProject"
              :createCallback="createProject"
              :deleteCallback="deleteProject"
              :fetchCallback="fetchProjects"
              :isReadOnly="isStandardControlling"
            />
          </b-col>
        </b-row> </template
    ></b-tabs>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs';
import { mapGetters, mapActions } from 'vuex';
import { getYesNoStateColor, getProjectStateColor } from '@/helpers/colors';
import ProjectTechnicalReview from '@/views/Projects/ProjectTechnicalReview';
import ProjectInvestmentDeductionAllowance from '@/views/Projects/ProjectInvestmentDeductionAllowance';
import EmployerApproval from '@/views/Projects/EmployerApproval';
import ProjectMeasurementReport from '@/views/Projects/ProjectMeasurementReport';
import ProjectAcceptanceReport from '@/views/Projects/ProjectAcceptanceReport';
import ProjectServiceReport from '@/views/Projects/ProjectServiceReport';
import ProjectSolarPlant from '@/views/Projects/ProjectSolarPlant';
import ProjectInternal from '@/views/Projects/ProjectInternal';
import ProjectCustomer from '@/views/Projects/ProjectCustomer';
import ProjectAccounting from '@/views/Projects/ProjectAccounting';
import ProjectRegistration from '@/views/Projects/ProjectRegistration';
import ProjectCardOverview from '@/views/Projects/ProjectCardOverview';
// import ProjectCardCommunication from '@/views/Projects/ProjectCardCommunication';
import ProjectCardState from '@/views/Projects/ProjectCardState';
import ProjectDocuments from '@/views/Projects/ProjectDocuments';
import ProjectBank from '@/views/Projects/ProjectBank';
import ProjectInstallation from '@/views/Projects/ProjectInstallation';
import ProjectTimeline from '@/views/Projects/ProjectTimeline';
import ButtonBar from '@/components/ButtonBar.vue';
import { findTabIndexByKey } from '@/helpers/projectTabs';
// import ProjectMaterial from '@/views/Projects/Items/ProjectMaterial';
import ProjectMaterials from '@/views/Projects/Items/ProjectMaterials';
import ProjectQualityReport from '@/views/Projects/ProjectQualityReport';
import ProjectAssessmentReport from '@/views/Projects/ProjectAssessmentReport';
import ProjectCommunication from '@/views/Projects/ProjectCommunication';
import ProjectAuditLog from './ProjectAuditLog.vue';
import ProjectPowerCompany from '@/views/Projects/ProjectPowerCompany';
import ProjectSalesReport from '@/views/Projects/ProjectSalesReport';
// import ProjectCardPosts from '@/views/Projects/ProjectCardPosts';
import ProjectCardInfo from '@/views/Projects/ProjectCardInfo';
export default {
  name: 'ProjectEditPage',
  components: {
    Breadcrumbs,
    ProjectTechnicalReview,
    ProjectInvestmentDeductionAllowance,
    EmployerApproval,
    ProjectMeasurementReport,
    ProjectSolarPlant,
    ProjectInternal,
    ProjectCustomer,
    ProjectAccounting,
    ButtonBar,
    ProjectRegistration,
    ProjectCardOverview,
    // ProjectCardCommunication,
    ProjectCardState,
    ProjectDocuments,
    ProjectBank,
    ProjectInstallation,
    ProjectAcceptanceReport,
    ProjectServiceReport,
    ProjectTimeline,
    // ProjectMaterial,
    ProjectMaterials,
    ProjectQualityReport,
    ProjectAssessmentReport,
    ProjectAuditLog,
    ProjectCommunication,
    // ProjectCardPosts,
    ProjectPowerCompany,
    ProjectSalesReport,
    ProjectCardInfo,
  },
  props: {
    projectNumber: {
      required: false,
      default: null,
    },
    projectTab: {
      type: String,
      required: false,
      default: '',
    },
    // referer: { default: '', type: String },
  },
  data() {
    return {
      number: null,
      user: {},
      buttonsDisabled: false,
      filteredProjectAttachmentTypes: [],
      tabIndex: 0,
      referer: null,
      selectedAppointment: null,
    };
  },
  filters: {
    toDate(value) {
      // console.log(value);
      return value ? new Date(value) : null;
    },
  },
  computed: {
    ...mapGetters('auth', [
      'isAdmin',
      'isClient',
      'getCurrentUser',
      'isGuest',
      'isStandardControlling',
    ]),
    ...mapGetters([
      'getFlashMessage',
      'getProject',
      'getProjectByNumber',
      'getPowerCompanies',
      'getSolarPanels',
      'getStorageSystems',
      'getMeasurementPointOperators',
      'getEnumValues',
    ]),

    isNew() {
      return this.project.id === '_new';
    },

    buttons() {
      return {
        showPreviousButton:
          this.isAdmin && this.project && this.project.previousProjectNumber ? true : false,
        showNextButton:
          this.isAdmin && this.project && this.project.nextProjectNumber ? true : false,
        showDeleteButton: false,
        // this.isAdmin && this.project && this.project.number !== '_new' ? true : false,
      };
    },

    netRegistrationSentAt: {
      get() {
        return this.$calendarFormat(this.project.netRegistrationSentAt);
      },
      set(value) {
        this.project.netRegistrationSentAt = value;
      },
    },
    injectionConfirmationReceivedAt: {
      get() {
        return this.$calendarFormat(this.project.injectionConfirmationReceivedAt);
      },
      set(value) {
        this.project.injectionConfirmationReceivedAt = value;
      },
    },
    operationRegistrationSentAt: {
      get() {
        return this.$calendarFormat(this.project.operationRegistrationSentAt);
      },
      set(value) {
        this.project.operationRegistrationSentAt = value;
      },
    },
    completionMessageSentAt: {
      get() {
        return this.$calendarFormat(this.project.completionMessageSentAt);
      },
      set(value) {
        this.project.completionMessageSentAt = value;
      },
    },
    powerCompanyClearanceAt: {
      get() {
        return this.$calendarFormat(this.project.powerCompanyClearanceAt);
      },
      set(value) {
        this.project.powerCompanyClearanceAt = value;
      },
    },
    project() {
      return this.getProjectByNumber(this.number);
    },
    /**
     * Workaround for the legacy roles; remove when the project blocks of the role matrix take effect
     * (docs/work/2026-10-first-block).
     */
    canSeeProjectInternalTabs() {
      return !this.isStandardControlling;
    },
    showIabCalculationTab() {
      return this.project?.businessModelState === 'INVESTVMENT_DEDUCTION_TAX_SAVING_MODEL';
    },
    projectNumberByClientContext() {
      let number = '';
      if (this.project.number === '_new') {
        number = this.$t('_new');
      }

      if (
        this.isClient &&
        this.project.number &&
        this.project.number !== '_new' &&
        this.project.clientProjectNumber
      ) {
        number = this.project.clientProjectNumber.toString().padStart(4, '0');
      }

      if (this.isAdmin && this.project.number && this.project.number !== '_new') {
        number = this.project.number.toString().padStart(4, '0');
      }
      return number;
    },
    breadcrumbs() {
      return [
        { name: 'Home', route: { name: 'home' } },
        { name: 'Projekte', route: { name: 'ProjectsPaginatedPage' } },
        {
          name: this.projectNumberByClientContext,
        },
      ];
    },
    /**
     * @todo DRY
     */
    invalidFeedbackNA() {
      return 'Voraussetzung NA';
    },
  },
  methods: {
    ...mapActions([
      'initProject',
      'fetchProjects',
      'fetchProjectByNumber',
      'updateProject',
      'createProject',
      'deleteProject',
      'fetchOriginalInstallationTypes',
      'fetchPowerCompanies',
      'fetchSolarPanels',
      'fetchStorageSystems',
      'uploadProjectAttachment',
      'deleteProjectAttachment',
      'fetchMeasurementPointOperators',
      'fetchEnumValues',
      'fetchReportTemplateImages',
    ]),
    setActiveTab(key) {
      const renderedTabs = this.$refs.tabs
        .getTabs()
        .map((tab) => ({ key: tab.$attrs['data-key'] }));
      const tabIndex = findTabIndexByKey(renderedTabs, key);
      if (tabIndex === -1) {
        console.log(`Tab with key "${key}" not found.`);
        return;
      }
      this.tabIndex = tabIndex;
    },
    isDisabled(e) {
      e.preventDefault();
    },
    yesNoColor(state) {
      return getYesNoStateColor(state);
    },
    projectStateColor(state) {
      return getProjectStateColor(state);
    },

    /** @todo changes to project are lost */
    async onDeleteProjectAttachment(event) {
      await this.deleteProjectAttachment({
        projectId: this.project.id,
        attachmentId: event.attachmentId,
        attachmentType: event.attachmentType,
      });
    },

    async setup() {
      await this.fetchOriginalInstallationTypes();
      await this.fetchEnumValues('ProjectAttachmentType');
      await this.fetchEnumValues('EmployerProjectAttachmentType');
      await this.fetchPowerCompanies();
      await this.fetchSolarPanels();
      await this.fetchStorageSystems();
      await this.fetchMeasurementPointOperators();
      await this.fetchReportTemplateImages();

      this.filteredProjectAttachmentTypes = this.isClient
        ? this.getEnumValues('EmployerProjectAttachmentType')
        : this.getEnumValues('ProjectAttachmentType');
    },

    async setupClientContext() {},

    async setupAdminContext() {},

    /**
     * check requirementsValidState to show mark input fields
     * @todo DRY
     */
    requirementsValidState(field) {
      if (!this.project.netRegistrationRequirements) return false;

      const requirement = this.project.netRegistrationRequirements.find(
        (requirement) => requirement.name === field
      );
      return requirement && requirement.fullfilledState === 'YES' ? true : false;
    },
  },
  async mounted() {
    if (this.isClient) {
      await this.setupClientContext();
    }
    if (this.isAdmin) {
      await this.setupAdminContext();
    }
    await this.setup();

    if (!this.project && this.projectNumber) {
      this.number = this.projectNumber;
      await this.fetchProjectByNumber(this.number).then((errors) => {
        if (errors && errors.length > 0 && errors[0].extensions.code === 'FORBIDDEN') {
          this.$router.push('/403');
        }
      });
    } else {
      this.initProject();
      this.number = '_new';
    }

    if (this.isClient) {
      this.project.client = this.getCurrentUser.client;
    }

    // tabs render after the project is set
    await this.$nextTick();

    /**
     * Go to installation tab if coming from RouteSchedulingPage
     */
    if (this.referer && this.referer.name === 'RouteSchedulingPage') {
      this.setActiveTab('installation');
    }
    if (this.projectTab) {
      this.setActiveTab(this.projectTab);
    }
  },

  /**
   * if only parameters of the route have changed the component will be reused
   * we have to update all the data before the route updates
   * @param to
   * @param from
   * @param next
   */
  async beforeRouteUpdate(to, from, next) {
    if (to.params.projectNumber && to.params.projectNumber !== this.projectNumber) {
      this.$store.commit('SET_PROJECT_DEFAULT_STATE');
      this.number = to.params.projectNumber;
      await this.fetchProjectByNumber(this.number);
      await this.setup();
      next();
    } else {
      next(false);
    }
  },

  /**
   * Save referer to current view
   */
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.referer = from;
    });
  },
};
</script>

<style scoped lang="scss">
.spinner {
  top: 4px;
  position: relative;
}

.project-cards-tight {
  margin-right: -0.25rem;
  margin-left: -0.25rem;
}

.project-cards-tight .card {
  margin-right: 0.25rem;
  margin-left: 0.25rem;
}

.customer-card {
  max-width: 50rem;
}
.solar-plant-card {
  max-width: 35rem;
}
.calculations-card {
  max-width: 25rem;
}
.solar-plant-data-grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1em;
  row-gap: 0.7em;
}
.calculations-grid-container {
  display: grid;
  grid-template-columns: 5fr 1fr;
  column-gap: 1em;
  row-gap: 0.7em;
}
.form-grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container div {
  padding: 0.4em;
}
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container-left div {
  padding: 0.4em;
}
.form-grid-container-left .label {
  text-align: right;
}

.trash-installation-appointment {
  text-align: right;
}
.trash-installation-appointment i {
  cursor: pointer;
}
legend {
  position: relative;
}
.button-right {
  position: absolute;
  display: inline-block;
  right: 0;
  top: 2px;
}

li > .badge {
  font-size: 0.8em;
  padding: 3px 4px 4px 4px;
}
.ck-content {
  min-height: 80px;
}
</style>
